import type { GridEventListener } from "@mui/x-data-grid";
import { text, multi, single, Relations } from "consts/setups";
import { FilterVariant } from "consts/filters";
import { Filters as UserFilters } from "consts/users";
import { RoleName } from "consts/auth";
import { PER_PAGE_SIZE } from "consts/transactions";
import useFetchFeatureResource from "hooks/useFetchFeatureResource";
import useSessionEffect from "hooks/useSessionEffect";
import { getUsers } from "features/users/api";
import { changeUsersLoadingState, changeUsersSlice } from "features/users/slice";
import useSetupsActions from "features/setups/useActions";
import { useResourceContext } from "ui/widgets/Resource";
import { RequestQueryMapper } from "util/request-query-mapper";

const generalSetupEffectSymbol = Symbol('generalSetupEffect');

export default function useGeneralSetups() {
    const { onOpen } = useResourceContext();

    const onRowClick: GridEventListener<'rowClick'> = ({ row }) => onOpen(row);

    const filterTypes = new Map<FilterVariant, string[]>([
        [FilterVariant.Text, text],
        [FilterVariant.MultiOption, multi],
        [FilterVariant.SingleOption, single]
    ]);
    const {
        getSetups: getResource,
        downloadCsv,
    } = useSetupsActions();

    const getUsersList = useFetchFeatureResource({
        shouldFetchAll: true,
        perPage: PER_PAGE_SIZE,
        resourceRequest: (queryString?: string) => getUsers(
            RequestQueryMapper
                .from(queryString)
                .doesntHaveRelations(Relations.UsersMerchantsPivot)
                .containsIn(UserFilters.roleName, RoleName.Admin)
                .containsIn(UserFilters.roleName, RoleName.OpsSalesAdmin)
                .containsIn(UserFilters.roleName, RoleName.FinanceAdmin)
                .containsIn(UserFilters.roleName, RoleName.MerchantAdmin)
                .containsIn(UserFilters.roleName, RoleName.ComplianceAdmin)
                .containsIn(UserFilters.roleName, RoleName.TechAdmin)
                .containsIn(UserFilters.roleName, RoleName.Employee)
                .toString()
        ),
        changeLoadingState: changeUsersLoadingState,
        changeSlice: changeUsersSlice
    });

    useSessionEffect(() => {
        getUsersList();
    }, generalSetupEffectSymbol);

    return {
        downloadCsv,
        getResource,
        onRowClick,
        filterTypes
    };
};
